/**
 * Company: SPHMedia
 * Description: Global Enterprise Vertical Layout
 */

import { ReactElement } from "react";
import { RouteFactory } from "@app/routePaths";
import { VerticalProps } from "@app/types/Verticals";
import { getSectionJSONLD } from "@app/util/helpers";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import PulseEntry from "@pages/Pulse/components/PulseEntry/PulseEntry";
import KeywordStoriesRow from "@pages/Section/components/VerticalMeta/KeywordStoriesRow";
import LatestStoriesRow from "@pages/Section/components/VerticalMeta/LatestStoriesRow";
import TopStoriesRow from "@pages/Section/components/VerticalMeta/TopStoriesRow";
import GlobalEnterpriseBanner from "@pages/Section/layouts/Verticals/GlobalEnterprise/components/GlobalEnterpriseBanner";

import GlobalEnterpriseNewsletterDescription from "./components/GlobalEnterpriseNewsletterDescription";

export default function GlobalEnterpriseVertical({
  data: { entity, title, topStories, additional, seoFooterData },
  mostReadArticles,
}: VerticalProps): ReactElement {
  return (
    <div className="w-full">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish", "abm"]}
        path={RouteFactory.globalEnterprise}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <MetaTags
        title={title}
        description="THE BUSINESS TIMES Global Enterprise - Find Global Enterprise News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times."
        ogType="article"
        slug={RouteFactory.globalEnterprise}
        jsonLd={getSectionJSONLD(entity)}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="mt-6 px-3">
        <GlobalEnterpriseBanner />

        <TopStoriesRow articles={topStories} verticalName="ge" />

        <LatestStoriesRow
          articles={topStories.slice(4, 9)}
          seeMoreLink="/international/global/latest"
          verticalName="ge"
          mostReadStories={mostReadArticles}
        />

        <KeywordStoriesRow
          seeMoreLink="/keywords/asia-pacific"
          data={additional?.[0]}
          verticalName="ge"
          verticalBlockTitle="ASIA-PACIFIC"
        />
      </Container>

      <NewsletterSignUpPanel
        rootClassName="mb-3"
        descriptionComponent={<GlobalEnterpriseNewsletterDescription />}
        newsletter={BT_NEWSLETTERS.filter(
          (newsletter) =>
            newsletter.type === NEWSLETTER_TYPE.MORNING ||
            newsletter.type === NEWSLETTER_TYPE.EVENING
        )}
        btnColors="bg-verticals-ge text-white hover:bg-white hover:text-verticals-ge"
        linkColors="text-verticals-ge"
      />

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
}
